// WebSocket 连接状态
enum CONNECTION_STATUS {
  CONNECTING,
  OPEN,
  CLOSED,
}

const HEARTBEAT_INTERVAL = 10 * 1000;

const HEARTBEAT_TYPE = 1;

// WebSocket 客户端类
export class WebSocketClient {
  private websocket!: WebSocket;

  private url: string;

  private heartTimerId!: NodeJS.Timeout;

  private isBeating = true;

  private heartStopedCount = 0;

  status: CONNECTION_STATUS = CONNECTION_STATUS.CLOSED;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private messageHandle: (message: any) => void = () => {};

  constructor(url: string) {
    this.url = url;

    this._onOpen = this._onOpen.bind(this);

    this._onClose = this._onClose.bind(this);

    this._onMessage = this._onMessage.bind(this);

    this._onError = this._onError.bind(this);

    this.open = this.open.bind(this);

    this.open();
  }

  private _onOpen(_event: WebSocketEventMap['open']) {
    this.status = CONNECTION_STATUS.OPEN;
  }

  private _onClose(_event: WebSocketEventMap['close']) {
    this.status = CONNECTION_STATUS.CLOSED;
  }

  private _onMessage(event: WebSocketEventMap['message']) {
    try {
      const message = JSON.parse(event.data);

      if (message.type === HEARTBEAT_TYPE) {
        this.isBeating = true; // 正在跳动
        this.heartStopedCount = 0;
      } else {
        this.messageHandle(message);
      }
    } catch (error) {
      // JSON 解析失败，忽略该消息
      // eslint-disable-next-line no-console
      console.warn('[WebSocketClient] 消息解析失败:', error);
    }
  }

  private _onError(_event: WebSocketEventMap['error']) {
    this.status = CONNECTION_STATUS.CLOSED;
  }

  sendMessage(message: string | Record<string | number | symbol, unknown>) {
    if (this.status !== CONNECTION_STATUS.OPEN) {
      // eslint-disable-next-line no-console
      console.warn('[WebSocketClient] WebSocket 未连接，无法发送消息');
      return;
    }
    this.websocket.send(typeof message === 'string' ? message : JSON.stringify(message));
  }

  onMessage<T extends Record<string | number | symbol, unknown>>(callback: (message: T) => void) {
    this.messageHandle = callback;
  }

  offMessage() {
    this.messageHandle = () => {};
  }

  destroy() {
    this.close();
    // 清理心跳定时器（如果存在）
    if (this.heartTimerId) {
      clearInterval(this.heartTimerId);
      this.heartTimerId = undefined as unknown as NodeJS.Timeout;
    }
    // 移除所有事件监听器
    if (this.websocket) {
      this.websocket.removeEventListener('open', this._onOpen);
      this.websocket.removeEventListener('close', this._onClose);
      this.websocket.removeEventListener('message', this._onMessage);
      this.websocket.removeEventListener('error', this._onError);
    }
    // 清空消息处理器
    this.messageHandle = () => {};
  }

  open() {
    if (this.status !== CONNECTION_STATUS.CLOSED) {
      return this;
    }
    this.heartStopedCount = 0;

    this.websocket = new WebSocket(this.url);
    this.status = CONNECTION_STATUS.CONNECTING;

    this.websocket.addEventListener('open', this._onOpen);

    this.websocket.addEventListener('close', this._onClose);

    this.websocket.addEventListener('message', this._onMessage);

    this.websocket.addEventListener('error', this._onError);

    // this.heartbeatDetection();
  }

  // 心跳检测，如果失败，则执行重连机制
  // 注释：此方法暂未启用，保留以备后续使用
  private heartbeatDetection() {
    // 心跳检测
    this.sendMessage({ type: HEARTBEAT_TYPE });
    this.heartTimerId = setInterval(() => {
      if (!this.isBeating) {
        if (this.heartStopedCount >= 3) {
          // 心跳检测发现已经断线，发起重连
          this.close();
          this.open();
        } else {
          this.heartStopedCount++;
          this.sendMessage({ type: HEARTBEAT_TYPE });
        }
      } else {
        this.sendMessage({ type: HEARTBEAT_TYPE });
      }
    }, HEARTBEAT_INTERVAL);
  }

  close() {
    if (this.status !== CONNECTION_STATUS.CLOSED) {
      this.websocket?.close();
    }
    this.status = CONNECTION_STATUS.CLOSED;
    clearInterval(this.heartTimerId);
  }
}
